/**
 * RemoteQuizService - Fetches and caches quiz configurations and questions from Firestore
 *
 * This service enables pushing new quizzes without app updates by storing quiz data
 * in Firestore instead of hardcoded JS files.
 *
 * Firestore Collections:
 * - quiz-configs/{quizType} - Quiz configuration documents
 * - quiz-questions/{quizType}/questions/{questionId} - Quiz questions
 *
 * Caching Strategy:
 * - Quiz configs: Cached for 1 hour, refreshed in background
 * - Questions: Cached per quiz for 30 minutes
 * - Falls back to static data if Firestore fetch fails
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  orderBy,
} from "firebase/firestore";
import { db } from "./firebase.config";
import dataCache from "./DataCache";

// Cache TTL constants
const CONFIG_CACHE_TTL = 60 * 60 * 1000; // 1 hour
const QUESTIONS_CACHE_TTL = 30 * 60 * 1000; // 30 minutes
const QUIZ_LIST_CACHE_TTL = 60 * 60 * 1000; // 1 hour
const CATEGORIES_CACHE_TTL = 60 * 60 * 1000; // 1 hour

class RemoteQuizService {
  constructor() {
    this.initialized = false;
    this.initPromise = null;
  }

  /**
   * Initialize the service by fetching all quiz configs
   * Called once on app startup
   */
  async init() {
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this._fetchAllConfigs();
    await this.initPromise;
    this.initialized = true;
    return this.initPromise;
  }

  /**
   * Fetch all quiz configurations from Firestore
   * Returns null if Firestore is empty or on error (triggers static fallback in callers)
   */
  async _fetchAllConfigs() {
    const cacheKey = "remote-quiz-configs-all";
    const cached = dataCache.get(cacheKey);

    if (cached) {
      return cached;
    }

    try {
      const configsRef = collection(db, "quiz-configs");
      const snapshot = await getDocs(configsRef);

      if (snapshot.empty) {
        console.log("📦 No remote quiz configs found, using static fallback");
        return null; // Return null to trigger static fallback in callers
      }

      const configs = {};
      snapshot.forEach((doc) => {
        const data = doc.data();
        configs[doc.id] = {
          ...data,
          quizType: doc.id,
        };
      });

      dataCache.set(cacheKey, configs, CONFIG_CACHE_TTL);
      console.log(
        `📦 Loaded ${Object.keys(configs).length} remote quiz configs`
      );
      return configs;
    } catch (error) {
      console.error("Failed to fetch remote quiz configs:", error);
      // Return null to trigger static fallback
      return null;
    }
  }

  /**
   * Get a single quiz configuration by quiz type
   * @param {string} quizType - The quiz type identifier (e.g., "personality-check")
   * @returns {Object|null} - Quiz configuration or null if not found
   */
  async getQuizConfig(quizType) {
    const cacheKey = `remote-quiz-config-${quizType}`;
    const cached = dataCache.get(cacheKey);

    if (cached) {
      return cached;
    }

    try {
      const docRef = doc(db, "quiz-configs", quizType);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const config = {
          ...docSnap.data(),
          quizType: docSnap.id,
        };
        dataCache.set(cacheKey, config, CONFIG_CACHE_TTL);
        return config;
      }

      return null;
    } catch (error) {
      console.error(`Failed to fetch quiz config for ${quizType}:`, error);
      return null;
    }
  }

  /**
   * Get all quiz configurations (for home page display)
   * @returns {Object|null} - Object with quiz type keys and config values, null if no remote data
   */
  async getAllQuizConfigs() {
    if (!this.initialized) {
      await this.init();
    }

    const cacheKey = "remote-quiz-configs-all";
    const cached = dataCache.get(cacheKey);

    if (cached) {
      return cached;
    }

    return this._fetchAllConfigs();
  }

  /**
   * Get quiz list for home page display, sorted by displayOrder
   * @returns {Array} - Array of quiz display info objects, empty if no remote data
   */
  async getQuizListForDisplay() {
    const cacheKey = "remote-quiz-list-display";
    const cached = dataCache.get(cacheKey);

    if (cached) {
      return cached;
    }

    const configs = await this.getAllQuizConfigs();

    // If no remote configs, return empty array to trigger static fallback
    if (!configs) {
      return [];
    }

    // Transform configs into display format and sort
    const displayList = Object.entries(configs)
      .filter(([_, config]) => config.enabled !== false) // Filter out disabled quizzes
      .map(([quizType, config]) => ({
        id: quizType,
        title: config.titleWord || quizType,
        description:
          config.description ||
          config.displayInfo?.description ||
          `Take the ${quizType} quiz`,
        iconName: config.displayInfo?.iconName || config.iconName || "Quiz",
        background: config.gradientColors?.background || "#3B96FF",
        transitionBackground:
          config.gradientColors?.transitionBackground || "#8800FF",
        startURL: config.firstQuestionRoute || `/quiz/${quizType}/question/1`,
        displayOrder: config.displayOrder || 999,
        category: config.category || null,
        subcategory: config.subcategory || null,
      }))
      .sort((a, b) => a.displayOrder - b.displayOrder);

    dataCache.set(cacheKey, displayList, QUIZ_LIST_CACHE_TTL);
    return displayList;
  }

  /**
   * Get questions for a specific quiz
   * @param {string} quizType - The quiz type identifier
   * @returns {Array} - Array of question objects sorted by id
   */
  async getQuizQuestions(quizType) {
    const cacheKey = `remote-quiz-questions-${quizType}`;
    const cached = dataCache.get(cacheKey);

    if (cached) {
      return cached;
    }

    try {
      const questionsRef = collection(
        db,
        "quiz-questions",
        quizType,
        "questions"
      );
      const q = query(questionsRef, orderBy("id"));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        console.log(
          `📦 No remote questions found for ${quizType}, using static fallback`
        );
        return null; // Will trigger static fallback in UniversalQuestionPage
      }

      const questions = [];
      snapshot.forEach((doc) => {
        questions.push({
          ...doc.data(),
          _docId: doc.id,
        });
      });

      // Sort by numeric id (in case orderBy didn't work with string ids)
      questions.sort((a, b) => parseInt(a.id) - parseInt(b.id));

      dataCache.set(cacheKey, questions, QUESTIONS_CACHE_TTL);
      console.log(
        `📦 Loaded ${questions.length} remote questions for ${quizType}`
      );
      return questions;
    } catch (error) {
      console.error(`Failed to fetch questions for ${quizType}:`, error);
      return null; // Will trigger static fallback
    }
  }

  /**
   * Get a single question by quiz type and question ID
   * @param {string} quizType - The quiz type identifier
   * @param {string} questionId - The question ID
   * @returns {Object|null} - Question object or null if not found
   */
  async getQuestion(quizType, questionId) {
    const questions = await this.getQuizQuestions(quizType);

    if (!questions) {
      return null;
    }

    return questions.find((q) => String(q.id) === String(questionId)) || null;
  }

  /**
   * Get category filters for home page, sorted by displayOrder
   * @returns {Array} - Array of category filter objects
   */
  async getCategoryFilters() {
    const cacheKey = "remote-quiz-categories";
    const cached = dataCache.get(cacheKey);

    if (cached) {
      return cached;
    }

    try {
      const categoriesRef = collection(db, "quiz-categories");
      const snapshot = await getDocs(categoriesRef);

      if (snapshot.empty) {
        console.log("📦 No remote categories found");
        return [];
      }

      const categories = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        if (data.enabled !== false) {
          categories.push({
            id: doc.id,
            label: data.label || doc.id,
            displayOrder: data.displayOrder || 999,
          });
        }
      });

      // Sort by displayOrder
      categories.sort((a, b) => a.displayOrder - b.displayOrder);

      dataCache.set(cacheKey, categories, CATEGORIES_CACHE_TTL);
      console.log(`📦 Loaded ${categories.length} remote categories`);
      return categories;
    } catch (error) {
      console.error("Failed to fetch remote categories:", error);
      return [];
    }
  }

  /**
   * Invalidate all quiz-related caches
   * Call this when you know data has changed
   */
  invalidateCache() {
    dataCache.invalidate("remote-quiz-*");
    dataCache.delete("remote-quiz-categories");
    this.initialized = false;
    this.initPromise = null;
    console.log("📦 Remote quiz cache invalidated");
  }

  /**
   * Invalidate cache for a specific quiz
   * @param {string} quizType - The quiz type to invalidate
   */
  invalidateQuizCache(quizType) {
    dataCache.delete(`remote-quiz-config-${quizType}`);
    dataCache.delete(`remote-quiz-questions-${quizType}`);
    dataCache.delete("remote-quiz-configs-all");
    dataCache.delete("remote-quiz-list-display");
    console.log(`📦 Cache invalidated for quiz: ${quizType}`);
  }

  /**
   * Check if a quiz exists (either remote or static)
   * @param {string} quizType - The quiz type to check
   * @returns {boolean}
   */
  async quizExists(quizType) {
    const config = await this.getQuizConfig(quizType);
    return config !== null;
  }

  /**
   * Preload quiz data for faster access
   * @param {string} quizType - The quiz type to preload
   */
  async preloadQuiz(quizType) {
    await Promise.all([
      this.getQuizConfig(quizType),
      this.getQuizQuestions(quizType),
    ]);
  }
}

// Create singleton instance
const remoteQuizService = new RemoteQuizService();

export default remoteQuizService;
